import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import styles from "../../BusinessStep/BusinessStep.module.css";
import stepStyles from "./StepShell.module.css";
import StepTwo from "../../BusinessStep/StepTwo";
import NewModal from "../../NewModal/NewModal";
import AnimatedButton from "../../AnimatedButton/AnimatedButton";
import PopUp from "../../Popup/Popup";
import { secureStorage } from "../../../utils/secureStorage";
import { API_BASE_URL } from "../../../Store/apiStore";
import { markSetupStep } from "../../../lib/setupScore";

const BUSINESS_EMAIL_MAX_LENGTH = 40;

// BusinessVerifyStep — post-purchase wrapper around the existing
// BusinessStep StepTwo. Reuses the GMB search + detailed form + confirm
// modal verbatim so the UI is identical to the legacy /business-step
// flow. Differences from the legacy parent:
//   - We skip StepOne (business type) and StepThree (agent language).
//   - On "Continue" we PUT /updateKnowledeBase/:businessId instead of
//     creating a new agent — the agent already exists from the demo
//     flow, we're only enriching its business record.
//   - Pre-fills formData from agentCtx.business so the user sees what
//     the demo captured earlier.

export default function BusinessVerifyStep({
  onNext,
  onBack,
  onSkip,
  agentCtx,
  refreshAgentCtx,
}) {
  // Refs mirroring BusinessStep.js usage.
  const stepTwoRef = useRef(null);
  const modalConfirmedRef = useRef(false);
  const justSelectedGMBRef = useRef(false);

  const [showDetailedForm, setShowDetailedForm] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prefillDone, setPrefillDone] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState("success");
  const [popupMessage, setPopupMessage] = useState("");

  // Retry-modal state for the Retell sync (PATCH /agentV2/updateAgent).
  // Rolling out a fresh business name/address to the agent's Retell LLM
  // can fail (network, Retell 5xx, schema mismatch) — when that happens
  // we open a modal so the user can retry instead of silently advancing
  // and ending up with an agent that still greets with the old name.
  const [retellRetryModalOpen, setRetellRetryModalOpen] = useState(false);
  const [retellRetryError, setRetellRetryError] = useState("");
  const [retellRetryBusy, setRetellRetryBusy] = useState(false);

  // Same formData shape BusinessStep passes into StepTwo.
  const [formData, setFormData] = useState({
    businessType: "",
    customBusiness: "",
    businessSubtype: "",
    displayBusinessName: "",
    googleListing: "",
    noGoogleListing: false,
    businessUrl: "",
    noBusinessWebsite: false,
    placeDetails: null,
    sitemapUrls: [],
    selectedSitemapUrls: [],
    isGMBSelected: false,
  });

  // updateFields — routes updates into both React state and secureStorage
  // so StepTwo's internal secureStorage reads stay in sync (same contract
  // BusinessStep.js provides).
  const updateFormData = (updates) => {
    if ("isGMBSelected" in updates && updates.isGMBSelected === true) {
      modalConfirmedRef.current = false;
      justSelectedGMBRef.current = true;
    }
    setFormData((prev) => {
      const next = { ...prev, ...updates };
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "") secureStorage.remove(k);
        else secureStorage.set(k, v);
      });
      if (updates.placeDetails)
        secureStorage.set("placeDetailsExtract", updates.placeDetails);
      if (updates.selectedSitemapUrls)
        secureStorage.set("selectedSiteMapUrls", updates.selectedSitemapUrls);
      return next;
    });
  };

  const updateBusinessEmail = (email) => {
    const businessEmail = String(email || "").slice(0, BUSINESS_EMAIL_MAX_LENGTH);
    const nextPlaceDetails = {
      ...(formData.placeDetails || {}),
      email: businessEmail,
    };

    updateFormData({ placeDetails: nextPlaceDetails });
  };

  // ── Pre-populate from secureStorage first (retains user edits when
  //    they navigate back to this step), then fall back to agent context
  //    (business row from the demo flow) for the first visit. ──
  useEffect(() => {
    if (agentCtx?.loading || prefillDone) return;

    // 1) secureStorage — wins when present, since it's either a prior
    //    visit to this step or an in-flight edit that wasn't saved yet.
    const stashedPlace = secureStorage.get("placeDetailsExtract", null);
    const stashedName = secureStorage.get("displayBusinessName", "");
    const stashedUrl = secureStorage.get("businessUrl", "");
    const stashedAbout = secureStorage.get("aboutBusinessForm", {}) || {};
    const hasStash = !!(
      (stashedPlace && stashedPlace.businessName) ||
      stashedName
    );

    if (hasStash) {
      setFormData((prev) => ({
        ...prev,
        displayBusinessName: stashedName || stashedPlace?.businessName || "",
        businessUrl: stashedUrl || "",
        noBusinessWebsite: Boolean(stashedAbout?.noBusinessWebsite),
        placeDetails: stashedPlace || null,
        // Override the disabled state if they are verified so they can search again easily
        noGoogleListing: false, 
        isGMBSelected: !stashedAbout?.noGoogleListing && !!stashedPlace,
      }));
      // Keep the search box visible
      setShowDetailedForm(false);

      setPrefillDone(true);
      return;
    }

    // 2) No stash yet — pre-fill from agentCtx.business.
    const b = agentCtx?.business;
    if (!b) {
      setPrefillDone(true);
      return;
    }

    // Build a placeDetails-shaped object so StepTwo's detailed form
    // renders populated. `knowledge_base_texts` sometimes holds the
    // richer address/hours payload from the earlier listing pull.
    let kb = {};
    try {
      kb =
        typeof b.knowledge_base_texts === "string"
          ? JSON.parse(b.knowledge_base_texts)
          : b.knowledge_base_texts || {};
    } catch {
      kb = {};
    }

    const businessName = b.businessName || b.googleBusinessName || "";
    const website = b.webUrl || b.website || kb.website || "";

    // Build the legacy placeDetails shape
    const addressStr = b.address1 || b.address || kb.address || "";
    const placeDetails = {
      businessName,
      phone: b.phoneNumber || b.phone || kb.phone || "",
      email: b.businessEmail || b.email || kb.email || "",
      address: addressStr,
      website,
      rating: kb.rating || "",
      totalRatings: kb.totalRatings || "",
      hours: kb.hours || [],
      businessStatus: kb.businessStatus || "",
      categories: kb.categories || [],
      city: b.city || kb.city || "",
      state: b.state || kb.state || "",
      country: b.country || kb.country || "",
      state_code: b.state_code || kb.state_code || "",
      country_code: b.country_code || kb.country_code || "",
    };

    const prefill = {
      displayBusinessName: businessName,
      businessUrl: website,
      noBusinessWebsite: !website,
      placeDetails,
      googleListing: "",
      noGoogleListing: false,
      isGMBSelected: false,
    };

    setFormData((prev) => ({ ...prev, ...prefill }));

    // Targeted clear — remove only the BusinessVerify-related keys so
    // we don't wipe unrelated wizard state. The earlier blanket
    // `secureStorage.clear?.()` was the cause of the "form data
    // disappears on first fill" rare bug — if anything triggered this
    // effect to re-evaluate (parent re-render after refreshAgentCtx,
    // a strict-mode double-invoke in dev), it nuked everything the
    // user had just typed across the wizard.
    [
      "googleListing",
      "isGMBSelected",
      "showNoGMBCheckbox",
      "noGoogleListing",
      "aboutBusinessForm",
      "placeDetailsExtract",
      "businessUrl",
      "websiteValidatedUrl",
      "displayBusinessName",
      "selectedSiteMapUrls",
      "selectedSiteMapUrls_backup",
      "urls",
      "viewSiteMap",
    ].forEach((k) => secureStorage.remove(k));

    secureStorage.set("placeDetailsExtract", placeDetails);
    secureStorage.set("displayBusinessName", businessName);

    const aboutForm = {
      noGoogleListing: false,
      noBusinessWebsite: !website,
      businessUrl: website || "",
    };
    secureStorage.set("aboutBusinessForm", aboutForm);

    setIsProcessing(false);
    setShowDetailedForm(false);
    setPrefillDone(true);
  }, [agentCtx?.loading, prefillDone]);

  // Auto-open the "Confirm your business details" modal when the user
  // picks a GMB result from search. Mirrors BusinessStep.js behavior.
  useEffect(() => {
    if (!justSelectedGMBRef.current) return;
    if (formData.noGoogleListing) return;
    if (isModalOpen) return;
    if (isProcessing) return;

    const hasRealDetails = !!(
      formData.placeDetails?.businessName &&
      formData.placeDetails?.phone &&
      formData.placeDetails?.address
    );
    if (hasRealDetails) {
      justSelectedGMBRef.current = false;
      setIsModalOpen(true);
    }
  }, [
    formData.placeDetails?.businessName,
    formData.placeDetails?.phone,
    formData.placeDetails?.address,
    isProcessing,
  ]);

  // Has-required-data gate for the Continue button.
  const hasRequiredData = () => {
    if (!showDetailedForm) {
      return (
        formData.displayBusinessName?.trim().length > 0 ||
        formData.noGoogleListing
      );
    }
    return formData.placeDetails?.businessName?.trim().length > 0;
  };

  const validate = async () => {
    if (!stepTwoRef.current?.validate) return true;
    const result = await stepTwoRef.current.validate();
    if (result === true) return true;

    const labels = {
      googleListing: "Business Selection",
      businessName: "Business Name",
      phone: "Phone Number",
      address: "Address",
      businessUrl: "Website URL",
      email: "Business Email",
    };
    const errs = result?.errors || {};
    const missing = Object.keys(errs)
      .filter((k) => k !== "email" && errs[k])
      .map((k) => labels[k] || k);
    const emailErr = errs.email;

    let msg = "Please complete all required fields";
    if (missing.length && emailErr)
      msg = `Required fields missing: ${missing.join(" | ")}\nBusiness Email: ${emailErr}`;
    else if (missing.length)
      msg = `Required fields missing: ${missing.join(" | ")}`;
    else if (emailErr)
      msg = emailErr;

    setPopupType("failed");
    setPopupMessage(msg);
    setShowPopup(true);
    return false;
  };

  // Push the new business context to Retell (LLM prompt + agent
  // greeting). Without this, the local DB row updates but the live
  // agent keeps greeting with the old business name/address. Mirrors
  // the payload shape used by /Editbusinessdetails when an existing
  // agent edits its business — backend's agentV2/updateAgent handler
  // does the rollback-safe LLM + agent + KB rebuild.
  const syncRetellAgent = async () => {
    const agentId =
      agentCtx?.agentId ||
      agentCtx?.agent?.agent_id ||
      sessionStorage.getItem("SelectAgentId");
    if (!agentId) return { ok: true }; // nothing to sync against

    const place = formData.placeDetails || {};
    const addressDetails = {
      businessName: place.businessName || formData.displayBusinessName || "",
      phone: place.phone || "",
      email: place.email || "",
      hours: place.hours || [],
      address: place.address || "",
      formattedAddress: place.address || "",
      city: place.city || "",
      state: place.state || "",
      state_code: place.state_code || "",
      country: place.country || "",
      country_code: place.country_code || "",
      postalCode: place.postal_code || "",
      street_number: place.street_number || "",
      route: place.route || "",
    };

    const payload = {
      agent_id: agentId,
      businessName: place.businessName || formData.displayBusinessName || "",
      googleBusinessName: formData.displayBusinessName || "",
      googleListing: formData.googleListing || "",
      businessWebsite: formData.noBusinessWebsite
        ? null
        : formData.businessUrl || "",
      sitemapUrls: formData.sitemapUrls || [],
      selectedSitemapUrls: formData.noBusinessWebsite
        ? []
        : (formData.selectedSitemapUrls || []).filter(
            (item) => item?.checkedStatus === true,
          ),
      businessPhone: place.phone || "",
      businessEmail: place.email || "",
      country_code: place.country_code || "",
      businessAddress: JSON.stringify({ addressDetails }),
    };

    try {
      const token = localStorage.getItem("token") || "";
      await axios.patch(
        `${API_BASE_URL}/agentV2/updateAgent`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return { ok: true };
    } catch (err) {
      console.error(
        "[BusinessVerifyStep] Retell sync failed:",
        err?.response?.data || err?.message || err,
      );
      return {
        ok: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Could not sync the agent with the new business details.",
      };
    }
  };

  const saveBusinessDetails = async () => {
    const businessId =
      agentCtx?.business?.businessId ||
      sessionStorage.getItem("SelectAgentBusinessId");

    if (!businessId) return true;

    const place = formData.placeDetails || {};
    const aboutForm = secureStorage.get("aboutBusinessForm", {}) || {};

    // Use FormData to match EditBusinessDetail.js / Multi-part backend expectations
    const fd = new FormData();
    fd.append("businessName", place.businessName || formData.displayBusinessName || "");
    fd.append("address1", place.address || "");
    fd.append("phoneNumber", place.phone || "");
    fd.append("webUrl", formData.businessUrl || "");
    fd.append("googleUrl", formData.googleListing || "");
    fd.append("googleBusinessName", formData.displayBusinessName || "");
    fd.append("city", place.city || "");
    fd.append("state", place.state || "");
    fd.append("country", place.country || "");
    fd.append("state_code", place.state_code || "");
    fd.append("country_code", place.country_code || "");
    fd.append("postal_code", place.postal_code || "");
    fd.append("street_number", place.street_number || "");
    fd.append("agentId", agentCtx?.agentId || "");
    fd.append("isGoogleListing", aboutForm.noGoogleListing ? "true" : "false");
    fd.append("isWebsiteUrl", aboutForm.noBusinessWebsite ? "true" : "false");

    // Construct the knowledge_base_texts blob
    const kbt = {
      phone: place.phone || "",
      email: place.email || "",
      address: place.address || "",
      city: place.city || "",
      state: place.state || "",
      country: place.country || "",
      state_code: place.state_code || "",
      country_code: place.country_code || "",
      hours: place.hours || [],
      categories: place.categories || [],
      website: formData.businessUrl || "",
    };
    fd.append("knowledge_base_texts", JSON.stringify(kbt));

    try {
      const token = localStorage.getItem("token") || "";
      await axios.patch(
        `${API_BASE_URL}/businessDetails/updateKnowledeBase/${businessId}`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // 🔥 REFRESH the parent context so next steps see the updated phone!
      if (typeof refreshAgentCtx === "function") {
        await refreshAgentCtx();
      }
      // Retell sync is the second leg — local save above is the source
      // of truth, this just makes the live agent reflect it. Failure
      // surfaces a retry modal; success is silent.
      const retellRes = await syncRetellAgent();
      if (!retellRes.ok) {
        setRetellRetryError(retellRes.error || "Sync failed.");
        setRetellRetryModalOpen(true);
        return false; // block wizard advance until retry succeeds or user dismisses
      }
      /*
       * Setup Score: businessConfirmed (15 pts). Fired here on the
       * happy path — local save succeeded AND Retell sync succeeded.
       * Idempotent server-side, so the retry-modal path also calling
       * onNext (handleRetellRetry / handleRetellSkip) doesn't risk
       * a double credit if the user lands here twice.
       */
      try {
        markSetupStep(
          "businessConfirmed",
          agentCtx?.agentId || agentCtx?.agent?.agent_id,
        );
      } catch (scoreErr) {
        console.warn(
          "[BusinessVerifyStep] businessConfirmed mark failed (non-fatal):",
          scoreErr?.message || scoreErr,
        );
      }
      return true;
    } catch (err) {
      console.error("[BusinessVerifyStep] save failed:", err);
      return true; // Don't block wizard Progress on local save failures (existing behavior)
    }
  };

  // Triggered from the retry modal — re-attempts only the Retell sync
  // (local PATCH already succeeded by this point). Closes the modal +
  // advances on success, leaves modal open + shows the new error on
  // failure.
  const handleRetellRetry = async () => {
    if (retellRetryBusy) return;
    setRetellRetryBusy(true);
    const res = await syncRetellAgent();
    setRetellRetryBusy(false);
    if (res.ok) {
      setRetellRetryModalOpen(false);
      setRetellRetryError("");
      onNext();
    } else {
      setRetellRetryError(res.error || "Sync failed.");
    }
  };

  // Lets the user proceed without a successful Retell sync if they're
  // really stuck (e.g. Retell is down). Local DB has the new details so
  // a manual retry from /edit-business-detail later will recover.
  const handleRetellSkip = () => {
    setRetellRetryModalOpen(false);
    setRetellRetryError("");
    onNext();
  };

  const handleContinue = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const ok = await validate();
      if (!ok) {
        if (!showDetailedForm) setShowDetailedForm(true);
        setIsProcessing(false);
        return;
      }

      // GMB selected but details haven't loaded yet — stay put.
      if (!formData.noGoogleListing && formData.isGMBSelected) {
        const hasReal = !!(
          formData.placeDetails?.businessName &&
          formData.placeDetails?.phone &&
          formData.placeDetails?.address
        );
        if (hasReal) {
          // saveBusinessDetails returns false when the Retell sync
          // failed and the retry modal is now open — defer onNext
          // to the modal's resolve callbacks (handleRetellRetry /
          // handleRetellSkip).
          const ok = await saveBusinessDetails();
          setIsProcessing(false);
          if (ok) onNext();
          return;
        }
      }

      // Typed text but didn't pick GMB — mirror BusinessStep: expand
      // the detailed form so they can fill the rest manually.
      if (!formData.noGoogleListing && !formData.isGMBSelected) {
        const typedName = formData.displayBusinessName || "";
        const cleared = {
          businessName: typedName,
          address: "",
          phone: "",
          internationalPhone: "",
          email: "",
          website: "",
          rating: "",
          totalRatings: "",
          hours: [],
          businessStatus: "",
          categories: [],
        };
        updateFormData({
          placeDetails: cleared,
          displayBusinessName: typedName,
          noGoogleListing: true,
          noBusinessWebsite: false,
          businessUrl: "",
          isGMBSelected: false,
        });
        secureStorage.set("placeDetailsExtract", cleared);
        if (typedName) secureStorage.set("displayBusinessName", typedName);
        secureStorage.remove("businessUrl");
        secureStorage.remove("websiteValidatedUrl");
        const aboutForm = secureStorage.get("aboutBusinessForm", {});
        aboutForm.noGoogleListing = true;
        aboutForm.noBusinessWebsite = false;
        secureStorage.set("aboutBusinessForm", aboutForm);
        setShowDetailedForm(true);
        setIsProcessing(false);
        return;
      }

      // noGoogleListing path — all fields must be present.
      if (formData.noGoogleListing) {
        const hasName = !!(
          formData.placeDetails?.businessName || formData.displayBusinessName
        );
        const hasPhone = !!formData.placeDetails?.phone;
        const hasAddr = !!formData.placeDetails?.address;
        const hasSite = formData.noBusinessWebsite || !!formData.businessUrl;
        if (!hasName || !hasPhone || !hasAddr || !hasSite) {
          setShowDetailedForm(true);
          setPopupType("failed");
          setPopupMessage("Please fill all required fields before continuing.");
          setShowPopup(true);
          setIsProcessing(false);
          return;
        }
        const ok = await saveBusinessDetails();
        setIsProcessing(false);
        if (ok) onNext();
        return;
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const isContinueDisabled = loading || isProcessing || !hasRequiredData();

  if (agentCtx?.loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
        Loading your business details…
      </div>
    );
  }

  return (
    <>
      {/* Back / Skip live in the wizard shell footer — this step just
          focuses on the form content. */}

      {/* Render StepTwo without BusinessStep's .containerBox/.stage/.container
          wrappers. Those use position:absolute + overflow:hidden for the
          legacy slide animation, which breaks inside the wizard's normal
          flow layout. A plain padded box is all we need here. */}
      {/* Step content — standard wizard padding, 160px bottom to clear
          the fixed CTA bar + wizard footer nav bar stacked above. */}
      <div
        style={{
          maxWidth: 500,
          margin: "0 auto",
          padding: "24px 20px 160px",
          fontFamily: '"Poppins", system-ui, -apple-system, sans-serif',
          pointerEvents: isProcessing ? "none" : "auto",
          opacity: isProcessing ? 0.6 : 1,
          transition: "opacity 0.3s ease",
        }}
      >
        <StepTwo
          ref={stepTwoRef}
          data={formData}
          updateFields={updateFormData}
          showDetailedForm={showDetailedForm}
          setShowDetailedForm={setShowDetailedForm}
          setLoading={setLoading}
          setIsProcessing={setIsProcessing}
        />
      </div>

      {/* Fixed CTA bar — sits at bottom:64px (just above the wizard footer
          nav which is 64px tall). Uses StepShell classes so position is
          consistent with every other wizard step. */}
      <div className={stepStyles.ctaBar}>
        <div className={stepStyles.ctaInner}>
          <AnimatedButton
            label={loading ? "Saving..." : "Continue →"}
            onClick={() => {
              if (
                formData.placeDetails &&
                !formData.businessUrl &&
                !formData.noBusinessWebsite
              ) {
                updateFormData({ noBusinessWebsite: true });
                const aboutForm = secureStorage.get("aboutBusinessForm", {});
                aboutForm.noBusinessWebsite = true;
                delete aboutForm.businessUrl;
                secureStorage.set("aboutBusinessForm", aboutForm);
                secureStorage.remove("businessUrl");
                handleContinue();
                return;
              }
              handleContinue();
            }}
            disabled={isContinueDisabled}
            positionType="relative"
          />
        </div>
      </div>

      <NewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Confirm your business details"
        height="auto"
      >
        <div className={styles.confirmBox}>
          <div className={styles.infoDiv}>
            <div className={styles.warningBox}>
              <img src="/svg/info_green.svg" alt="info_green" />
              <p>
                We'll use this information to <b>greet callers</b> and{" "}
                <b>answer basic questions.</b>
              </p>
            </div>
            <img
              onClick={() => {
                setIsModalOpen(false);
                setShowDetailedForm(true);
              }}
              src="/svg/edit-icon3.svg"
              alt="edit_icon"
            />
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.introName}>Business Name</span>
            <span className={styles.introDetail}>
              {formData.placeDetails?.businessName ||
                formData.displayBusinessName ||
                "N/A"}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.introName}>Phone Number</span>
            <span className={styles.introDetail}>
              {formData.placeDetails?.phone || "N/A"}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.introName}>City / location</span>
            <span className={styles.introDetail}>
              {formData.placeDetails?.address || "N/A"}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.introName}>Business Email</span>
            <input
              type="email"
              className={styles.inlineEmailInput}
              style={{ color: formData.placeDetails?.email ? '#24252C' : '#979797' }}
              placeholder="Add your email? (for notifications)"
              value={formData.placeDetails?.email || ""}
              maxLength={BUSINESS_EMAIL_MAX_LENGTH}
              onChange={(e) =>
                updateFormData({
                  placeDetails: {
                    ...formData.placeDetails,
                    email: e.target.value.slice(0, BUSINESS_EMAIL_MAX_LENGTH),
                  },
                })
              }
            />
          </div>
          <div className={styles.confirmRow}>
            <span className={styles.introName}>Website</span>
            <span className={styles.introDetail}>
              {formData.businessUrl || "N/A"}
            </span>
          </div>

          <div className={styles.bothBtn}>
            <div className={styles.looksGoodBtn}>
              <AnimatedButton
                label="Looks good"
                positionType="relative"
                width="auto"
                onClick={async () => {
                  setIsProcessing(true);
                  const result = await stepTwoRef.current?.validate();
                  if (result !== true) {
                    setIsProcessing(false);
                    const errors = result?.errors || {};
                    const errorKeys = Object.keys(errors);

                    if (errorKeys.length === 1 && errorKeys[0] === 'email') {
                      setPopupType("failed");
                      setPopupMessage(errors.email || "Please enter a valid Business Email.");
                      setShowPopup(true);
                      return;
                    }

                    setIsModalOpen(false);
                    setShowDetailedForm(true);
                    if (!formData.noGoogleListing) {
                      setPopupType("failed");
                      setPopupMessage("Please fix the validation errors");
                      setShowPopup(true);
                    }
                    return;
                  }
                  modalConfirmedRef.current = true;
                  setIsModalOpen(false);
                  const ok = await saveBusinessDetails();
                  setIsProcessing(false);
                  if (ok) onNext();
                }}
              />
            </div>
          </div>
        </div>
      </NewModal>

      {showPopup && (
        <PopUp
          type={popupType}
          message={popupMessage}
          onClose={() => setShowPopup(false)}
        />
      )}

      {/* Retell sync retry modal — opens when /agentV2/updateAgent
          fails after a successful local PATCH. The local DB already
          has the new business details; we just need the Retell-side
          LLM/agent to catch up before the user proceeds (otherwise the
          live agent keeps greeting with the old name). */}
      <NewModal
        isOpen={retellRetryModalOpen}
        onClose={() => setRetellRetryModalOpen(false)}
        title="Couldn't update your agent"
        height="auto"
      >
        <div style={{ padding: "4px 4px 8px" }}>
          <p
            style={{
              fontSize: 14,
              color: "#374151",
              lineHeight: 1.55,
              margin: "0 0 14px",
            }}
          >
            Your business details were saved, but we couldn't refresh your
            agent's greeting + prompt with the new business name and address.
            Without this, the agent will keep greeting callers with the old
            details on the next call.
          </p>
          {retellRetryError && (
            <p
              style={{
                fontSize: 12,
                color: "#9b1c1c",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                padding: "8px 10px",
                borderRadius: 8,
                margin: "0 0 14px",
                wordBreak: "break-word",
              }}
            >
              {retellRetryError}
            </p>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginTop: 6,
            }}
          >
            <AnimatedButton
              label={retellRetryBusy ? "Retrying…" : "Retry sync"}
              positionType="relative"
              onClick={handleRetellRetry}
              isLoading={retellRetryBusy}
            />
            <button
              type="button"
              onClick={handleRetellSkip}
              disabled={retellRetryBusy}
              style={{
                background: "transparent",
                border: "none",
                fontSize: 13,
                color: "#6b7280",
                textDecoration: "underline",
                cursor: retellRetryBusy ? "not-allowed" : "pointer",
                padding: "8px 0",
                opacity: retellRetryBusy ? 0.5 : 1,
              }}
            >
              Continue anyway (I'll fix this later)
            </button>
          </div>
        </div>
      </NewModal>
    </>
  );
}
